import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, getDocs, updateDoc, deleteDoc, collection, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../AuthProvider';
import { useUI } from '../UIContext';
import { setDirtyFlag } from '../safeNavigate';

import PageWrapperAuto from '../components/PageWrapperAuto';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ConfirmLeaveModal from '../components/ConfirmLeaveModal';
import CustomTagInput from '../components/CustomTagInput';
import { useUserPrefs } from '../UserPrefsProvider';
import { normalizeTagsArray, normalizeTag, normalizeKey, normalizeType, keyRoot } from '../utils/normalize';
import { flushPendingWrites, addPendingWrite } from '../utils/flushPendingWrites';
import dexie from '../offlineDB';

export default function EditSessionPage() {
  const { inputStyle } = useUI();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { sessionId } = useParams();

  const [location, setLocation] = useState('');

  const [notes, setNotes] = useState('');
  const [startedAt, setStartedAt] = useState('');
  const [endedAt, setEndedAt] = useState('');
  const [tunes, setTunes] = useState([]);
  const [wasCreatedEmpty, setWasCreatedEmpty] = useState(false);
  const [customTags, setCustomTags] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [allTunes, setAllTunes] = useState([]);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterKey, setFilterKey] = useState('');
  const [filterGenre, setFilterGenre] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [pendingLeaveAction, setPendingLeaveAction] = useState(null);
  const { prefs } = useUserPrefs();
const { dexieReady } = useUI();

const toInputFormat = val => {
  let date;

  if (val?.seconds != null) {
    date = new Date(val.seconds * 1000);
  } else if (val?.toDate) {
    date = val.toDate();
  } else {
    date = new Date(val);
  }

  if (isNaN(date)) return '';

  // Convert to local datetime string: YYYY-MM-DDTHH:MM
  const pad = n => n.toString().padStart(2, '0');
  const local = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return local;
};




  useEffect(() => {
    if (!user) return;

    const unsubTunes = onSnapshot(collection(db, 'users', user.uid, 'tunes'), snapshot => {
      setAllTunes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const load = async () => {
      if (navigator.onLine) await flushPendingWrites(user);
      const local = await dexie.sessions.get(sessionId);
      const snap = !local && await getDoc(doc(db, 'users', user.uid, 'sessions', sessionId));
      const data = local || (snap?.exists() ? snap.data() : null);
console.log('[Session load] Raw startedAt:', data.startedAt);
console.log('[Session load] Raw endedAt:', data.endedAt);
console.log('[Session load] Raw tunes array:', data.tunes);

      if (!data) return;

setLocation(data.location || '');
      setNotes(data.notes || '');
      setStartedAt(toInputFormat(data.startedAt));
      setEndedAt(toInputFormat(data.endedAt));
      console.log('[Session load] Formatted startedAt:', toInputFormat(data.startedAt));
console.log('[Session load] Formatted endedAt:', toInputFormat(data.endedAt));

let sessionTunes = [];

// Try to pull from Dexie first (offline support)
if (dexieReady) {
  const allTunes = await dexie.tunes.toArray();
  sessionTunes = allTunes.filter(t =>
    Array.isArray(t.sessions) &&
    t.sessions.some(s => s.sessionId === sessionId)
  );
}

// Fallback to Firestore if nothing loaded from Dexie
if (sessionTunes.length === 0 && user) {
  const snap = await getDocs(collection(db, 'users', user.uid, 'tunes'));
  const allTunesFS = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  sessionTunes = allTunesFS.filter(t =>
    Array.isArray(t.sessions) &&
    t.sessions.some(s => s.sessionId === sessionId)
  );
}

setTunes(sessionTunes);



setCustomTags(data.customTags || []);
if (!data.location && (!data.tunes || data.tunes.length === 0)) setWasCreatedEmpty(true);
    };

    load();
    return () => unsubTunes();
  }, [user, sessionId]);

  const handleSave = async () => {
    if (!user) return;

    const updated = {
  location: location.trim() || 'Unnamed',
      notes: notes.trim(),
      startedAt: startedAt ? new Date(startedAt) : null,
      endedAt: endedAt ? new Date(endedAt) : null,
      tunes,
      customTags: normalizeTagsArray(customTags),
      updatedAt: new Date().toISOString()
    };

    const ref = doc(db, 'users', user.uid, 'sessions', sessionId);
    const prefsRef = doc(db, 'users', user.uid, 'preferences', 'settings');

    try {
      const snap = await getDoc(prefsRef);
      const oldTags = snap.exists() ? snap.data().customTags || [] : [];
      const mergedTags = Array.from(new Set([
        ...normalizeTagsArray(customTags),
        ...oldTags.map(normalizeTag)
      ]));

      if (navigator.onLine) {
        await updateDoc(prefsRef, { customTags: mergedTags });
        await updateDoc(ref, updated);
      } else {
        await addPendingWrite({
          id: 'settings',
          type: 'update',
          collection: 'preferences',
          docId: 'settings',
          data: { customTags: mergedTags }
        });
        await addPendingWrite({
          id: sessionId,
          type: 'update',
          collection: 'sessions',
          docId: sessionId,
          data: { id: sessionId, ...updated }
        });
      }

      await dexie.sessions.put({ id: sessionId, ...updated });

      setIsDirty(false);
      navigate(`/session/${sessionId}`);
      alert(navigator.onLine ? 'Session saved' : 'Saved offline. Will sync when online.');
    } catch (err) {
      console.error('Error saving session:', err);
      alert('Failed to save session');
    }
  };

  const handleRemove = id => {
    setTunes(prev => prev.filter(t => t.id !== id));
    setIsDirty(true);
  };

  const handleDragEnd = result => {
    if (!result.destination) return;
    const reordered = Array.from(tunes);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setTunes(reordered);
    setIsDirty(true);
  };

  const keyGroups = {};
  allTunes.forEach(t => {
    const nk = normalizeKey(t.key);
    if (!nk) return;
    const root = keyRoot(nk);
    if (!keyGroups[root]) keyGroups[root] = new Set();
    keyGroups[root].add(nk);
  });

  const genreOptions = [...new Set(allTunes.map(t => t.genre?.trim().toLowerCase()).filter(Boolean))].sort();
  const filtered = allTunes.filter(t => {
    const q = search.toLowerCase();
    const nk = normalizeKey(t.key || '');
    return (!searchQuery || Object.values(t).some(val => typeof val === 'string' && val.toLowerCase().includes(searchQuery.toLowerCase())))
      && (!q || t.title?.toLowerCase().includes(q))
      && (!filterType || normalizeType(t.type) === normalizeType(filterType))
      && (!filterKey || nk === filterKey || (filterKey.startsWith('root:') && keyRoot(nk) === filterKey.replace('root:', '')))
      && (!filterGenre || t.genre?.trim().toLowerCase() === filterGenre);
  });

  useEffect(() => {
    const handler = e => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  useEffect(() => setDirtyFlag(isDirty), [isDirty]);
  useEffect(() => () => { setIsDirty(false); setDirtyFlag(false); }, []);

  const formatTuneLabel = t => {
    const parts = [];
    if (t.type) parts.push(t.type);
    if (t.key) parts.push(t.key);
    return parts.length ? ` (${parts.join(', ')})` : '';
  };

  console.log('[Render] startedAt state:', startedAt);
console.log('[Render] endedAt state:', endedAt);

  return (
    <PageWrapperAuto>
      <h2>Edit Session</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '400px', margin: '0 auto 20px' }}>
<input
  style={inputStyle}
  value={location}
  onChange={e => { setLocation(e.target.value); setIsDirty(true); }}
  placeholder="Session location"
/>
        <textarea style={inputStyle} value={notes} onChange={e => { setNotes(e.target.value); setIsDirty(true); }} placeholder="Notes (optional)" rows={3} />
        <input style={inputStyle} type="datetime-local" value={startedAt} onChange={e => { setStartedAt(e.target.value); setIsDirty(true); }} />
        <input style={inputStyle} type="datetime-local" value={endedAt} onChange={e => { setEndedAt(e.target.value); setIsDirty(true); }} />
        <div>
          <h4 style={{ marginBottom: '0.25rem' }}>Tags</h4>
          <CustomTagInput tags={customTags} setTags={tags => { setCustomTags(tags); setIsDirty(true); }} suggestions={prefs?.customTags || []} inputStyle={inputStyle} />
        </div>
      </div>

     <h3>Tunes in Session</h3>
<DragDropContext onDragEnd={handleDragEnd}>
  <Droppable droppableId="tuneList">
    {(provided) => (
      <div
        ref={provided.innerRef}
        {...provided.droppableProps}
        style={{
          maxHeight: '200px',
          overflowY: 'auto',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '6px',
          backgroundColor: 'var(--card-bg, #fff)',
          color: 'inherit',
          minHeight: '40px'
        }}
      >
        {tunes.map((t, index) => (
          <Draggable key={`${t.id || t.title}-${index}`} draggableId={`${t.id || t.title}-${index}`} index={index}>
            {(provided) => {
              const style = {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'var(--item-bg, #f9f9f9)',
                color: 'var(--text-color, #000)',
                padding: '8px',
                borderBottom: '1px solid #ccc',
                ...provided.draggableProps.style
              };
              return (
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  style={style}
                >
                  <span className="text-scaled">
                    {t.title}{formatTuneLabel(t)}
                  </span>
                  <button onClick={() => handleRemove(t.id)} style={{ ...inputStyle, marginLeft: '10px' }}>×</button>
                </div>
              );
            }}
          </Draggable>
        ))}
        {provided.placeholder}
      </div>
    )}
  </Droppable>
</DragDropContext>


      <h3>Add Tunes</h3>
<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', maxWidth: '100%' }}>
<div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center', maxWidth: '600px' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by title" style={inputStyle} />
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search any field" style={inputStyle} />
        </div>
<div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center', maxWidth: '600px' }}>
          <select value={filterGenre} onChange={e => setFilterGenre(e.target.value)} style={inputStyle}>
            <option value="">All genres</option>
            {genreOptions.map((g, i) => <option key={i} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>)}
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={inputStyle}>
            <option value="">All types</option>
            {[...new Set(allTunes.map(t => normalizeType(t.type)).filter(Boolean))].sort().map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filterKey} onChange={e => setFilterKey(e.target.value)} style={inputStyle}>
            <option value="">All keys</option>
            {Object.keys(keyGroups).sort().map(root => (
              <optgroup key={root} label={root}>
                <option value={`root:${root}`}>{root} (All Modes)</option>
                {[...keyGroups[root]].sort().map(k => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '6px', maxHeight: '200px', overflowY: 'auto' }}>
          {filtered.map(item => {
            const inSession = tunes.find(t => t.id === item.id);
            return (
              <div key={item.id} onClick={() => {
                if (inSession) return;
                const newEntry = {
                  id: item.id,
                  title: item.title || '',
                  type: item.type || '',
                  key: item.key || ''
                };
                setTunes(prev => [...prev, newEntry]);
                setIsDirty(true);
              }}
                style={{
                  padding: '4px 0',
                  fontWeight: inSession ? 'bold' : 'normal',
                  backgroundColor: inSession ? '#f0f0f0' : 'transparent',
                  color: inSession ? '#999' : 'inherit',
                  cursor: inSession ? 'default' : 'pointer'
                }}>
                <span className="text-scaled">{item.title}{formatTuneLabel(item)}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', margin: '20px 0' }}>
        <button style={inputStyle} onClick={handleSave}>Save Session</button>
        <button style={{ ...inputStyle, backgroundColor: '#ddd', color: '#000' }} onClick={async () => {
          const cleanup = async () => {
            if (wasCreatedEmpty && user && sessionId) {
              const ref = doc(db, 'users', user.uid, 'sessions', sessionId);
              try {
                if (navigator.onLine) {
                  await deleteDoc(ref);
                  await dexie.sessions.delete(sessionId);
                } else {
                  await dexie.sessions.delete(sessionId);
                  await addPendingWrite({
                    id: sessionId,
                    type: 'delete',
                    collection: 'sessions',
                    docId: sessionId
                  });
                }
              } catch (err) {
                console.error('Failed to delete empty session:', err);
              }
            }
            navigate('/sessions');
          };

          if (isDirty) {
            setPendingLeaveAction(() => cleanup);
            setShowLeaveModal(true);
          } else {
            await cleanup();
          }
        }}>Cancel</button>
      </div>

      <ConfirmLeaveModal open={showLeaveModal} onCancel={() => setShowLeaveModal(false)} onConfirm={() => {
        setShowLeaveModal(false);
        setIsDirty(false);
        if (pendingLeaveAction) pendingLeaveAction();
      }} />
    </PageWrapperAuto>
  );
}
